/**
 * InsightsPage - Erasmus+ Knowledge Hub for SEO and content marketing
 * Blog/editorial layout with articles, categories, and soft CTAs
 * Full article content with Read More functionality
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as ArrowIcon,
  School as SchoolIcon,
  Lightbulb as IdeaIcon,
  TrendingUp as TrendingIcon,
  Groups as GroupsIcon,
  AccountBalance as InstitutionIcon,
  AccessTime as TimeIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  MenuBook as ArticleIcon
} from '@mui/icons-material';

interface InsightsPageProps {
  onBack: () => void;
  onShowContact?: () => void;
  onShowProjects?: () => void;
  onShowLanding?: () => void;
}

// Article categories
const CATEGORIES = [
  { id: 'understanding', label: 'Understanding Erasmus+', icon: <SchoolIcon />, color: '#003399' },
  { id: 'opportunities', label: 'Opportunities & Actions', icon: <IdeaIcon />, color: '#0066cc' },
  { id: 'development', label: 'Project Development', icon: <GroupsIcon />, color: '#004d99' },
  { id: 'strategy', label: 'Strategy & Impact', icon: <TrendingIcon />, color: '#002266' }
];

// Full articles with complete content
const ARTICLES = [
  {
    id: 1,
    title: 'What Is Erasmus Plus and Who Can Apply?',
    excerpt: 'A comprehensive introduction to the €26 billion European programme that funds education, training, youth, and sport projects across Europe and beyond.',
    category: 'understanding',
    readTime: 8,
    featured: true,
    content: {
      sections: [
        {
          title: 'What Is Erasmus Plus?',
          paragraphs: [
            'Erasmus Plus is the European Union programme that funds education, training, youth and cooperation projects across Europe and partner countries.',
            'With a total budget of €26 billion, Erasmus Plus supports organisations in developing skills and competences, building European partnerships, testing innovative educational models, and strengthening organisational capacity.',
            'Contrary to common belief, Erasmus Plus programme is not only for students.'
          ],
          bullets: [
            'Developing skills and competences',
            'Building European partnerships',
            'Testing innovative educational models',
            'Strengthening organisational capacity'
          ]
        },
        {
          title: 'Who Can Apply for Erasmus Plus?',
          paragraphs: [
            'Erasmus Plus is open to a wide range of organisations:'
          ],
          bullets: [
            'Universities and higher education institutions',
            'Schools and VET providers',
            'NGOs, foundations and associations',
            'Public bodies',
            'Private organisations active in education or training'
          ],
          note: 'You do not need previous EU project experience, a dedicated EU office, or to be a large institution. You need a solid project idea and a European approach.'
        },
        {
          title: 'Why Many Eligible Organisations Never Apply',
          paragraphs: [
            'Most organisations do not apply because Erasmus Plus seems complex, they lack internal project design capacity, or they wait for calls instead of planning.',
            'As a result, funding opportunities are lost before the call is even published.'
          ]
        }
      ],
      guidance: 'This article serves as your entry point to understanding Erasmus Plus. If your organisation falls into any of the eligible categories, you have potential access to significant EU funding. The key is to start planning before calls open.',
      cta: 'Not sure if your organisation is eligible for Erasmus Plus? We help organisations understand their potential and generate projects before submission.'
    }
  },
  {
    id: 2,
    title: 'Erasmus Plus Is Not Just for Students: Opportunities for Organisations',
    excerpt: 'Debunking the biggest misconception about Erasmus Plus and revealing the strategic opportunities available for organisations of all types.',
    category: 'understanding',
    readTime: 6,
    featured: false,
    content: {
      sections: [
        {
          title: 'The Biggest Misconception About Erasmus Plus',
          paragraphs: [
            'Erasmus Plus is often associated exclusively with student mobility. This perception is wrong and limiting.',
            'The majority of the Erasmus Plus budget is allocated to cooperation projects, organisational development, and innovation and capacity building.',
            'These actions are designed for organisations, not individuals.'
          ]
        },
        {
          title: 'What Organisations Can Do with Erasmus Plus',
          paragraphs: [
            'Through Erasmus Plus, organisations can:'
          ],
          bullets: [
            'Build long-term European partnerships',
            'Test new educational or training models',
            'Develop digital and inclusive solutions',
            'Strengthen internal competences'
          ],
          note: 'Erasmus Plus is a strategic development tool, not a one-off grant.'
        },
        {
          title: 'Why Strategy Matters More Than the Call',
          paragraphs: [
            'Successful organisations do not "chase calls". They build projects first, then submit them when calls open.',
            'This is where project generation becomes critical.'
          ]
        }
      ],
      guidance: 'Understanding that Erasmus Plus is primarily for organisations opens new strategic possibilities. The programme is designed to support institutional growth and European cooperation, not just individual mobility.',
      cta: 'Do you want to use Erasmus Plus strategically, not occasionally? We generate Erasmus Plus project concepts aligned with EU priorities.'
    }
  },
  {
    id: 3,
    title: 'How to Build a Strong Erasmus Plus Project',
    excerpt: 'The essential elements of a successful Erasmus Plus project and why preparation before the form matters most.',
    category: 'development',
    readTime: 10,
    featured: true,
    content: {
      sections: [
        {
          title: 'A Strong Erasmus Plus Project Starts Before the Form',
          paragraphs: [
            'A strong Erasmus Plus project is not built during submission. It is built months before, through clear objectives, European relevance, and coherent partnerships.',
            'Forms come last. Projects come first.'
          ]
        },
        {
          title: 'Key Elements of a Strong Project',
          paragraphs: [
            'A successful Erasmus Plus project includes:'
          ],
          bullets: [
            'A clear problem or need',
            'Measurable objectives',
            'European added value',
            'Realistic impact',
            'Sustainability beyond funding'
          ],
          note: 'Missing any of these elements reduces success probability.'
        },
        {
          title: 'Why Project Generation Reduces Risk',
          paragraphs: [
            'Project generation allows organisations to validate ideas early, align with Erasmus Plus priorities, avoid rushed proposals, and enter calls prepared.'
          ]
        }
      ],
      guidance: 'The difference between funded and rejected projects often lies in preparation quality. Projects developed strategically before calls open have significantly higher success rates.',
      cta: 'Do you already have an idea but don\'t know if it fits Erasmus Plus? We transform ideas into structured Erasmus Plus projects.'
    }
  },
  {
    id: 4,
    title: 'Erasmus Plus National Agencies: How the Evaluation Process Works',
    excerpt: 'Understanding the role of National Agencies and how the evaluation process works in each participating country.',
    category: 'strategy',
    readTime: 7,
    featured: true,
    content: {
      sections: [
        {
          title: 'What Is an Erasmus Plus National Agency?',
          paragraphs: [
            'Each participating country manages Erasmus Plus through its own National Agency, responsible for publishing calls, evaluating proposals, and awarding funding.',
            'There is no central EU submission authority.'
          ]
        },
        {
          title: 'Why This Matters for Organisations',
          paragraphs: [
            'Projects must be submitted to the correct National Agency, aligned with national priorities, and formally compliant.',
            'Strategic preparation before submission is essential.'
          ]
        },
        {
          title: 'Where Our Role Ends',
          paragraphs: [
            'We support organisations in project idea generation, concept structuring, and partnership building.',
            'Final submission and evaluation remain the responsibility of the National Agency.'
          ]
        }
      ],
      guidance: 'Understanding the decentralised nature of Erasmus Plus is crucial. Each country has its own priorities and evaluation criteria within the EU framework.',
      cta: 'Want to approach your National Agency with a solid project? We help you get there prepared.'
    }
  },
  {
    id: 5,
    title: 'Erasmus Plus Project Ideas Already Generated: How to Use Them',
    excerpt: 'How organisations can leverage pre-generated project concepts to accelerate their Erasmus Plus journey.',
    category: 'opportunities',
    readTime: 12,
    featured: true,
    content: {
      sections: [
        {
          title: 'What Does "Erasmus Plus Project Ideas Already Generated" Mean?',
          paragraphs: [
            'When organisations search for Erasmus Plus project ideas, they are usually looking for inspiration, concrete starting points, and validated concepts aligned with EU priorities.',
            'A generated Erasmus Plus project idea is not a generic example. It is a structured concept that already includes a clear problem or need, defined objectives, a European cooperation logic, and alignment with Erasmus Plus actions and priorities.',
            'These ideas are created before the call opens, not during submission.'
          ]
        },
        {
          title: 'Why Starting from a Generated Project Idea Matters',
          paragraphs: [
            'Most failed Erasmus Plus applications fail at concept level, not at form level.',
            'Starting from a generated project idea allows organisations to save time in the early design phase, reduce strategic and conceptual errors, align with Erasmus Plus priorities from the start, and focus on partnership and implementation.',
            'Instead of asking "What can we submit?", organisations can ask "Which project fits our mission and context?"'
          ]
        },
        {
          title: 'How Organisations Can Use Generated Projects',
          paragraphs: [
            'Generated project ideas can be used in different ways:'
          ],
          bullets: [
            'As a Coordinating Organisation: adapt the project to your national context, build the partnership, submit the proposal',
            'As a Partner Organisation: join an existing project concept, contribute expertise or target groups, participate without leading',
            'As a Strategic Planning Tool: explore future opportunities, plan medium-term European involvement, develop internal competences'
          ],
          note: 'Generated projects are starting points, not rigid templates.'
        },
        {
          title: 'What Makes a Generated Project "Erasmus Plus–Ready"',
          paragraphs: [
            'A project idea is Erasmus Plus–ready when it addresses a real, documented need, has clear European added value, fits specific Erasmus Plus actions (e.g. KA1, KA2), and is adaptable to different countries and organisations.',
            'This is exactly what project generation focuses on.'
          ]
        },
        {
          title: 'From Generated Project to Official Submission',
          paragraphs: [
            'Once a project is selected and adapted, it is developed into a full proposal, submitted to the relevant Erasmus Plus National Agency, and evaluated according to official criteria.',
            'Project generation ends before submission.'
          ]
        }
      ],
      guidance: 'Pre-generated projects represent a paradigm shift in how organisations approach EU funding. Instead of starting from zero, you start from a validated concept that already incorporates EU priorities and best practices.',
      cta: 'Looking for Erasmus Plus project ideas you can actually use? Access structured Erasmus Plus projects already generated and ready for development.'
    }
  },
  {
    id: 6,
    title: 'Building a European Partnership: What You Need to Know',
    excerpt: 'The logic of European cooperation, how to find partners, and what makes a consortium strong and complementary.',
    category: 'development',
    readTime: 9,
    featured: false,
    content: {
      sections: [
        {
          title: 'The Logic of European Partnerships',
          paragraphs: [
            'European partnerships in Erasmus Plus are not just about having partners in different countries. They are about creating complementary consortiums where each partner brings unique value.',
            'A strong partnership demonstrates clear roles, complementary expertise, and shared commitment to project objectives.'
          ]
        },
        {
          title: 'How to Find the Right Partners',
          paragraphs: [
            'Finding partners requires strategic thinking:'
          ],
          bullets: [
            'Start with your project needs, not with available partners',
            'Look for complementary expertise and target groups',
            'Consider geographical and sectoral diversity',
            'Evaluate commitment capacity and reliability'
          ]
        },
        {
          title: 'What Makes a Consortium Strong',
          paragraphs: [
            'Strong consortiums share common goals but bring diverse perspectives. They have clear governance structures and communication channels.',
            'The best partnerships are built on genuine collaboration, not just administrative convenience.'
          ]
        }
      ],
      guidance: 'Partnership quality is one of the most evaluated criteria in Erasmus Plus. A well-structured consortium with clear complementarity significantly increases funding chances.',
      cta: 'Need help building your European partnership? We can support you in structuring a strong consortium for your project.'
    }
  },
  {
    id: 7,
    title: 'Erasmus Plus as a Long-Term Strategy',
    excerpt: 'Why organisations should approach Erasmus Plus strategically, building capacity over time rather than chasing single grants.',
    category: 'strategy',
    readTime: 8,
    featured: false,
    content: {
      sections: [
        {
          title: 'Beyond the Single Grant Mindset',
          paragraphs: [
            'Many organisations approach Erasmus Plus as a one-time opportunity. This mindset limits potential and increases risk.',
            'Successful organisations view Erasmus Plus as a long-term strategic tool for institutional development and European positioning.'
          ]
        },
        {
          title: 'Building Erasmus Plus Capacity',
          paragraphs: [
            'Strategic organisations build internal capacity through:'
          ],
          bullets: [
            'Developing project management expertise',
            'Creating lasting European networks',
            'Accumulating track record and credibility',
            'Learning from each project cycle'
          ]
        },
        {
          title: 'The Compound Effect of European Projects',
          paragraphs: [
            'Each successful project opens doors to new opportunities. Networks expand, expertise deepens, and reputation grows.',
            'After 3-5 years of strategic involvement, organisations become natural leaders in their sectors.'
          ]
        }
      ],
      guidance: 'Erasmus Plus is most valuable when approached as a multi-year strategy rather than isolated grant applications. Consider your first project as the beginning of a European journey.',
      cta: 'Ready to develop a long-term Erasmus Plus strategy? Let us help you plan your European development path.'
    }
  },
  {
    id: 8,
    title: 'European Added Value: What Evaluators Look For',
    excerpt: 'Understanding the concept of European added value and how to demonstrate it convincingly in your project proposal.',
    category: 'strategy',
    readTime: 7,
    featured: false,
    content: {
      sections: [
        {
          title: 'What Is European Added Value?',
          paragraphs: [
            'European added value is one of the most critical evaluation criteria in Erasmus Plus. It asks: why does this project need to be European?',
            'Projects must demonstrate outcomes that could not be achieved through national or local action alone.'
          ]
        },
        {
          title: 'How to Demonstrate European Added Value',
          paragraphs: [
            'Strong European added value comes from:'
          ],
          bullets: [
            'Cross-border challenges requiring European solutions',
            'Transfer and adaptation of practices across contexts',
            'Comparative learning from different national systems',
            'Economies of scale through European cooperation',
            'Contribution to EU-wide priorities and goals'
          ]
        },
        {
          title: 'Common Mistakes to Avoid',
          paragraphs: [
            'Many projects fail to articulate clear European added value. They describe activities instead of impact, or they add partners without justifying why.',
            'Every European dimension in your project should answer: what does this add that national action cannot achieve?'
          ]
        }
      ],
      guidance: 'European added value is not just a box to tick—it is the fundamental justification for EU funding. Projects with weak European dimension rarely score high enough for funding.',
      cta: 'Need help articulating your project\'s European added value? We can help you strengthen this critical element.'
    }
  },
  {
    id: 9,
    title: 'Common Misconceptions About Erasmus Plus',
    excerpt: 'Erasmus Plus is not just student mobility. Debunking myths and clarifying what the programme really offers to organisations.',
    category: 'understanding',
    readTime: 5,
    featured: false,
    content: {
      sections: [
        {
          title: 'Myth 1: Erasmus Plus Is Only for Universities',
          paragraphs: [
            'Reality: Erasmus Plus is open to schools, NGOs, training centres, public bodies, and private organisations. Higher education is just one of many sectors.',
            'In fact, some of the largest funding lines are specifically for non-university organisations.'
          ]
        },
        {
          title: 'Myth 2: You Need Experience to Apply',
          paragraphs: [
            'Reality: First-time applicants are welcome and often successful. Many actions are specifically designed for newcomers to European projects.',
            'What matters is project quality, not organisational history.'
          ]
        },
        {
          title: 'Myth 3: Erasmus Plus Is Too Complex',
          paragraphs: [
            'Reality: While the programme has many options, each action has clear guidelines. The complexity is in choosing the right action, not in the application itself.',
            'Good preparation simplifies everything.'
          ]
        },
        {
          title: 'Myth 4: Small Organisations Cannot Compete',
          paragraphs: [
            'Reality: Small organisations often have advantages—they are more agile, more innovative, and closer to their target groups.',
            'Many successful projects are led by small organisations with big ideas.'
          ]
        }
      ],
      guidance: 'Understanding what Erasmus Plus really is—and is not—is the first step to accessing its opportunities. Do not let misconceptions prevent you from exploring your potential.',
      cta: 'Want to understand if Erasmus Plus is right for your organisation? Contact us for a free initial assessment.'
    }
  },
  {
    id: 10,
    title: '2024-2027 Priorities: Where Erasmus Plus Is Heading',
    excerpt: 'Digital transformation, inclusion, sustainability, and civic participation: the key priorities shaping funding decisions today.',
    category: 'strategy',
    readTime: 10,
    featured: false,
    content: {
      sections: [
        {
          title: 'The Four Horizontal Priorities',
          paragraphs: [
            'Erasmus Plus 2021-2027 emphasises four horizontal priorities that cut across all actions:'
          ],
          bullets: [
            'Inclusion and Diversity: reaching underrepresented groups and addressing barriers to participation',
            'Digital Transformation: developing digital skills and leveraging technology for learning',
            'Environment and Sustainability: addressing climate change and promoting green practices',
            'Participation in Democratic Life: strengthening civic engagement and European values'
          ]
        },
        {
          title: 'Why Priorities Matter for Your Project',
          paragraphs: [
            'Projects that clearly address these priorities score higher in evaluation. Alignment is not optional—it is strategic.',
            'However, alignment must be genuine, not superficial. Evaluators can distinguish between real commitment and token references.'
          ]
        },
        {
          title: 'Emerging Trends for 2024-2027',
          paragraphs: [
            'Beyond the official priorities, certain themes are gaining traction:'
          ],
          bullets: [
            'AI and emerging technologies in education',
            'Mental health and wellbeing',
            'Green skills and circular economy',
            'Cross-sectoral cooperation',
            'Micro-credentials and flexible learning'
          ]
        }
      ],
      guidance: 'Staying aligned with EU priorities is essential for funding success. Projects that anticipate where the programme is heading—not just where it is today—position themselves for success.',
      cta: 'Want to ensure your project aligns with current EU priorities? We can help you position your idea strategically.'
    }
  }
];

const InsightsPage: React.FC<InsightsPageProps> = ({ onBack, onShowContact, onShowProjects, onShowLanding }) => {
  useTheme();
  useMediaQuery('(max-width:900px)'); // Keep for potential future use
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<typeof ARTICLES[0] | null>(null);

  const filteredArticles = selectedCategory 
    ? ARTICLES.filter(a => a.category === selectedCategory)
    : ARTICLES;

  const featuredArticles = ARTICLES.filter(a => a.featured);

  const getCategoryInfo = (categoryId: string) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  // Article Detail View
  if (selectedArticle) {
    const catInfo = getCategoryInfo(selectedArticle.category);
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        {/* Navigation */}
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0, 51, 153, 0.1)'
          }}
        >
          <Toolbar>
            <Button
              startIcon={<BackIcon />}
              onClick={() => setSelectedArticle(null)}
              sx={{ color: '#003399' }}
            >
              Back to Insights
            </Button>
            <Typography 
              variant="h6" 
              onClick={onShowLanding}
              sx={{ 
                ml: 2, 
                flexGrow: 1, 
                color: '#003399', 
                fontWeight: 700,
                cursor: onShowLanding ? 'pointer' : 'default',
                '&:hover': onShowLanding ? { opacity: 0.8 } : {}
              }}
            >
              🎓 Erasmus+ Project Generator
            </Typography>
            <IconButton onClick={() => setSelectedArticle(null)} sx={{ color: '#003399' }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Article Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${catInfo.color} 0%, #0052cc 100%)`,
            py: { xs: 6, md: 10 },
            position: 'relative'
          }}
        >
          <Container maxWidth="md">
            <Chip 
              label={catInfo.label}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white', 
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.8rem' },
                fontWeight: 800,
                color: 'white',
                lineHeight: 1.2,
                mb: 2
              }}
            >
              {selectedArticle.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TimeIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 18 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {selectedArticle.readTime} min read
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Article Content */}
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            {/* Introduction */}
            <Typography variant="h6" sx={{ color: '#333', lineHeight: 1.8, mb: 4, fontStyle: 'italic' }}>
              {selectedArticle.excerpt}
            </Typography>

            <Divider sx={{ mb: 4 }} />

            {/* Content Sections */}
            {selectedArticle.content.sections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: catInfo.color, 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <ArticleIcon sx={{ fontSize: 24 }} />
                  {section.title}
                </Typography>
                
                {section.paragraphs.map((p, pIdx) => (
                  <Typography key={pIdx} sx={{ color: '#444', lineHeight: 1.9, mb: 2 }}>
                    {p}
                  </Typography>
                ))}

                {section.bullets && (
                  <Box sx={{ ml: 2, mb: 2 }}>
                    {section.bullets.map((bullet, bIdx) => (
                      <Box key={bIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: catInfo.color,
                          mt: 1,
                          flexShrink: 0
                        }} />
                        <Typography sx={{ color: '#444', lineHeight: 1.7 }}>{bullet}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {section.note && (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(0, 51, 153, 0.05)', 
                      borderLeft: `4px solid ${catInfo.color}`,
                      borderRadius: '0 8px 8px 0'
                    }}
                  >
                    <Typography sx={{ color: '#333', fontWeight: 500 }}>
                      💡 {section.note}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ))}

            {/* Editorial Guidance */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: '#FFFBEB', 
                border: '2px solid #FFCC00',
                borderRadius: 3,
                mb: 4
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#003399', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                📋 Editorial Note & Guidance
              </Typography>
              <Typography sx={{ color: '#444', lineHeight: 1.8 }}>
                {selectedArticle.content.guidance}
              </Typography>
            </Paper>

            {/* CTA Section */}
            <Box sx={{ 
              p: 4, 
              bgcolor: catInfo.color, 
              borderRadius: 3, 
              textAlign: 'center' 
            }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
                {selectedArticle.content.cta}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {onShowProjects && (
                  <Button
                    variant="contained"
                    onClick={onShowProjects}
                    sx={{
                      bgcolor: '#FFCC00',
                      color: '#003399',
                      fontWeight: 700,
                      px: 3,
                      '&:hover': { bgcolor: '#e6b800' }
                    }}
                  >
                    Explore Generated Projects
                  </Button>
                )}
                {onShowLanding && (
                  <Button
                    variant="outlined"
                    onClick={onShowLanding}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    Learn How We Work
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Related Articles */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399', mb: 3 }}>
              Continue Reading
            </Typography>
            <Grid container spacing={3}>
              {ARTICLES.filter(a => a.category === selectedArticle.category && a.id !== selectedArticle.id)
                .slice(0, 2)
                .map((article) => (
                  <Grid item xs={12} md={6} key={article.id}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: catInfo.color, transform: 'translateY(-2px)' }
                      }}
                      onClick={() => setSelectedArticle(article)}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {article.excerpt.substring(0, 100)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </Container>

        {/* Footer */}
        <Box sx={{ bgcolor: '#001a4d', py: 4 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Button
                variant="text"
                onClick={() => setSelectedArticle(null)}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Back to All Articles
              </Button>
              {onShowContact && (
                <Button
                  variant="text"
                  startIcon={<EmailIcon />}
                  onClick={onShowContact}
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Contact Us
                </Button>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  // Main Insights Page
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', fontFamily: '"Inter", "Segoe UI", sans-serif' }}>
      {/* Navigation */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 51, 153, 0.1)'
        }}
      >
        <Toolbar>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ color: '#003399' }}
          >
            Back
          </Button>
          <Typography 
            variant="h6" 
            onClick={onShowLanding}
            sx={{ 
              ml: 2, 
              flexGrow: 1, 
              color: '#003399', 
              fontWeight: 700,
              cursor: onShowLanding ? 'pointer' : 'default',
              '&:hover': onShowLanding ? { opacity: 0.8 } : {}
            }}
          >
            🎓 Erasmus+ Project Generator
          </Typography>
          {onShowContact && (
            <Button
              variant="text"
              onClick={onShowContact}
              sx={{ color: '#003399' }}
            >
              Contact
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: `
              radial-gradient(circle at 20% 50%, white 2px, transparent 2px),
              radial-gradient(circle at 80% 30%, white 1px, transparent 1px),
              radial-gradient(circle at 60% 70%, white 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px'
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              color: 'white',
              textAlign: 'center',
              mb: 3
            }}
          >
            Erasmus+ Insights
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              maxWidth: 800,
              mx: 'auto',
              mb: 2,
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            Erasmus Plus is more than a funding programme.
            <br />
            <strong>It is a European ecosystem of opportunities.</strong>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              maxWidth: 700,
              mx: 'auto',
              lineHeight: 1.8
            }}
          >
            News, articles and insights designed to help organisations understand 
            how Erasmus Plus works and how to approach it strategically.
          </Typography>
        </Container>
      </Box>

      {/* Target Audience */}
      <Box sx={{ bgcolor: 'white', py: 4, borderBottom: '1px solid #eee' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {[
              { icon: <InstitutionIcon />, text: 'Organisations approaching Erasmus+ for the first time' },
              { icon: <SchoolIcon />, text: 'Institutions seeking clarity and guidance' },
              { icon: <TrendingIcon />, text: 'Experienced actors looking for strategic insights' }
            ].map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                  <Box sx={{ color: '#003399' }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Category Filter */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 4 }}>
          <Chip
            label="All Articles"
            onClick={() => setSelectedCategory(null)}
            variant={selectedCategory === null ? 'filled' : 'outlined'}
            sx={{
              bgcolor: selectedCategory === null ? '#003399' : 'transparent',
              color: selectedCategory === null ? 'white' : '#003399',
              borderColor: '#003399',
              fontWeight: 600,
              '&:hover': { bgcolor: selectedCategory === null ? '#002266' : 'rgba(0,51,153,0.1)' }
            }}
          />
          {CATEGORIES.map((cat) => (
            <Chip
              key={cat.id}
              icon={cat.icon}
              label={cat.label}
              onClick={() => setSelectedCategory(cat.id)}
              variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedCategory === cat.id ? cat.color : 'transparent',
                color: selectedCategory === cat.id ? 'white' : cat.color,
                borderColor: cat.color,
                fontWeight: 600,
                '& .MuiChip-icon': { color: selectedCategory === cat.id ? 'white' : cat.color },
                '&:hover': { bgcolor: selectedCategory === cat.id ? cat.color : 'rgba(0,51,153,0.1)' }
              }}
            />
          ))}
        </Box>

        {/* Featured Articles (only when no category selected) */}
        {!selectedCategory && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399', mb: 3, textAlign: 'center' }}>
              Featured Articles
            </Typography>
            <Grid container spacing={3}>
              {featuredArticles.slice(0, 3).map((article) => {
                const catInfo = getCategoryInfo(article.category);
                return (
                  <Grid item xs={12} md={4} key={article.id}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        border: '2px solid',
                        borderColor: catInfo.color,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': { 
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(0,51,153,0.15)'
                        }
                      }}
                      onClick={() => setSelectedArticle(article)}
                    >
                      <CardActionArea sx={{ height: '100%', p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Chip 
                            label={catInfo.label} 
                            size="small"
                            sx={{ 
                              bgcolor: catInfo.color, 
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }} 
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#999' }}>
                            <TimeIcon sx={{ fontSize: 14 }} />
                            <Typography variant="caption">{article.readTime} min</Typography>
                          </Box>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 2, lineHeight: 1.3 }}>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, mb: 2 }}>
                          {article.excerpt}
                        </Typography>
                        <Button 
                          size="small" 
                          endIcon={<ArrowIcon />}
                          sx={{ color: catInfo.color, fontWeight: 600, p: 0 }}
                        >
                          Read Full Article
                        </Button>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* All Articles Grid */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399', mb: 3, textAlign: 'center' }}>
            {selectedCategory ? getCategoryInfo(selectedCategory).label : 'All Articles'}
            <Typography component="span" sx={{ color: '#999', fontWeight: 400, ml: 1 }}>
              ({filteredArticles.length})
            </Typography>
          </Typography>
          
          <Grid container spacing={3}>
            {filteredArticles.map((article) => {
              const catInfo = getCategoryInfo(article.category);
              return (
                <Grid item xs={12} sm={6} lg={4} key={article.id}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%',
                      bgcolor: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': { 
                        borderColor: catInfo.color,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                      }
                    }}
                    onClick={() => setSelectedArticle(article)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Chip 
                          label={catInfo.label} 
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderColor: catInfo.color,
                            color: catInfo.color,
                            fontWeight: 600,
                            fontSize: '0.65rem'
                          }} 
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#999' }}>
                          <TimeIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{article.readTime} min read</Typography>
                        </Box>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1.5, lineHeight: 1.4 }}>
                        {article.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, mb: 2 }}>
                        {article.excerpt}
                      </Typography>
                      <Button 
                        size="small" 
                        endIcon={<ArrowIcon />}
                        sx={{ color: catInfo.color, fontWeight: 600, p: 0 }}
                      >
                        Read More
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>

      {/* Content Purpose Section */}
      <Box sx={{ bgcolor: 'white', py: 8, mt: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#003399', textAlign: 'center', mb: 4 }}>
            What You'll Find Here
          </Typography>
          <Grid container spacing={2}>
            {[
              'Erasmus Plus explained in clear and accessible language',
              'Opportunities explored for different types of organisations',
              'Trends, priorities and European directions analysed',
              'Common doubts and misconceptions clarified',
              'Support for informed decision-making'
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%', 
                    bgcolor: '#003399', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                    mt: 0.5
                  }}>
                    ✓
                  </Box>
                  <Typography variant="body1" sx={{ color: '#333' }}>{item}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
            This is an informational space, updated regularly.
          </Typography>
        </Container>
      </Box>

      {/* Soft CTA Section */}
      <Box sx={{ py: 8, bgcolor: '#f0f4f8' }}>
        <Container maxWidth="md">
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center',
              bgcolor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 3
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399', mb: 2 }}>
              Do you have a project idea or a question about Erasmus Plus?
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              We can help you understand how to move forward.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {onShowLanding && (
                <Button
                  variant="contained"
                  endIcon={<ArrowIcon />}
                  onClick={onShowLanding}
                  sx={{
                    bgcolor: '#003399',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#002266' }
                  }}
                >
                  Learn How We Work
                </Button>
              )}
              {onShowProjects && (
                <Button
                  variant="outlined"
                  endIcon={<ArrowIcon />}
                  onClick={onShowProjects}
                  sx={{
                    borderColor: '#003399',
                    color: '#003399',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'rgba(0,51,153,0.05)' }
                  }}
                >
                  Explore Generated Projects
                </Button>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Institutional Footer */}
      <Box sx={{ bgcolor: '#001a4d', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
              Erasmus Plus projects are officially evaluated and funded by <strong>National Agencies</strong>, 
              which differ in each participating country. The role of this platform is to provide information, 
              guidance and project generation support, helping organisations approach Erasmus Plus in an 
              informed and structured way.
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 4 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              © {new Date().getFullYear()} Erasmus+ Project Generator. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {onShowContact && (
                <Button
                  variant="text"
                  startIcon={<EmailIcon />}
                  onClick={onShowContact}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { color: 'white', bgcolor: 'transparent' }
                  }}
                >
                  Contact Us
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default InsightsPage;
